import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import HeadItem from '@components/table/head_item';
import Checkbox from '@components/common/checkbox';
import Typography from '@components/common/typography';

export type TColumn = {
    label?: string;
    key?: string;
    width?: number;
    isSort?: boolean;
    render?: (val: any, row: any, index: number) => ReactNode | string;
    isRightAlignment?: boolean;
    filterOptions?: {
        value: string;
        label: string;
    }[];
    isSingleFiler?: boolean;
};

export type TRow = Record<string, any>;

export type TFilter = {
    [key: string]: string[];
};

export type TSort = {
    key: string;
    value: 'ASC' | 'DESC';
};

interface IProps<T = any> {
    columns: TColumn[];
    rows?: TRow[];
    onRowClick?: (data: T) => void;
    onSortChange?: (key: string, value: 'ASC' | 'DESC') => void;
    onFilterChange?: (key: TFilter) => void;
    sort?: TSort;
    filter?: TFilter;
    isActions?: boolean;
    emptyDescription?: string;
    selectKey?: string;
    selected?: string[];
    onSelectedChange?: (val: string[]) => void;
    isSingleSelect?: boolean;
    totalRows?: number;
    onLoadData?: () => void;
}

const cx = classNames.bind(styles);

const Table: FC<IProps> = ({
    columns,
    rows,
    onRowClick,
    onLoadData,
    onSortChange,
    sort,
    filter,
    onFilterChange,
    selectKey,
    selected,
    onSelectedChange,
    isSingleSelect,
    totalRows,
    emptyDescription,
    isActions = false
}) => {
    const loader = useRef(null);
    const [trigger, setTrigger] = useState(0);
    const rowStyle = useMemo(() => cx([{ isOnclick: !!onRowClick }]), [onRowClick]);

    function getValueByPath(obj: any, path?: string) {
        if (path) {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        }
        return '';
    }

    const handleSortChange = (key?: string) => {
        if (onSortChange && key) {
            if (sort?.key === key) {
                if (sort?.value === 'ASC') {
                    onSortChange(key, 'DESC');
                } else {
                    onSortChange(key, 'ASC');
                }
            } else {
                onSortChange(key, 'DESC');
            }
        }
    };

    const handleFilterChange = (key: string, value: string, column: TColumn) => {
        if (onFilterChange) {
            if (filter?.[key] && !column?.isSingleFiler) {
                if (filter[key]?.includes(value)) {
                    onFilterChange({
                        ...filter,
                        [key]: filter[key]?.filter((item) => item !== value)
                    });
                } else {
                    onFilterChange({
                        ...filter,
                        [key]: [...filter[key], value]
                    });
                }
            } else {
                onFilterChange({
                    ...filter,
                    [key]: [value]
                });
            }
        }
    };

    const handleChangeSelect = (value: string) => {
        if (onSelectedChange && selected && selectKey) {
            if (selected?.includes(value)) {
                onSelectedChange(selected?.filter((item) => item !== value));
            } else {
                if (isSingleSelect) {
                    onSelectedChange([value]);
                } else {
                    onSelectedChange([...selected, value]);
                }
            }
        }
    };

    useEffect(() => {
        const loadItems = () => {
            if (totalRows && onLoadData && rows && totalRows > rows.length && rows.length > 0) {
                onLoadData();
            }
        };
        loadItems();
    }, [trigger]);

    const handleObserver = useCallback(
        (entries: any) => {
            const target = entries[0];
            if (target.isIntersecting) {
                setTrigger((prev) => prev + 1);
            }
        },
        [rows]
    );

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const handleChangeSelectAllItems = () => {
        if (onSelectedChange && rows && selectKey) {
            if (selected?.length === rows?.length) {
                onSelectedChange([]);
            } else {
                const allValues = rows.map((row) => row?.[selectKey]);
                onSelectedChange(allValues);
            }
        }
    };

    return (
        <div className={styles.wrapper}>
            <table className={styles.table}>
                <thead className={cx([styles.header, { isActions, isSelect: !!selectKey }])}>
                    <tr>
                        {columns.map((column, i) => {
                            return (
                                <HeadItem
                                    key={i}
                                    handleFilterChange={handleFilterChange}
                                    column={column}
                                    selectKey={selectKey}
                                    filter={filter}
                                    handleSortChange={handleSortChange}
                                    sort={sort}
                                    isActions={isActions && columns.length - 1 === i}
                                    isAllSelected={selected?.length === rows?.length}
                                    onChangeAllSelected={handleChangeSelectAllItems}
                                />
                            );
                        })}
                    </tr>
                </thead>
                <tbody className={styles.body}>
                    {rows?.map((row, index) => {
                        return (
                            <tr key={index} className={rowStyle}>
                                {columns.map((column, i) => {
                                    const columnStyle = {
                                        textAlign: column.isRightAlignment
                                            ? 'right'
                                            : column?.key && row?.[column?.key] !== undefined
                                              ? 'left'
                                              : 'center',
                                        width: column.width,
                                        display: 'flex',
                                        justifyContent: column.isRightAlignment ? 'flex-end' : ''
                                    };
                                    return (
                                        <td key={i}>
                                            <div
                                                //@ts-ignore
                                                style={columnStyle}
                                                onClick={() =>
                                                    onRowClick &&
                                                    i !== columns.length - 1 &&
                                                    isActions &&
                                                    onRowClick(row)
                                                }>
                                                <div className={styles.select_wrapper}>
                                                    {selectKey && selectKey === column?.key && (
                                                        <Checkbox
                                                            checked={selected?.includes(
                                                                row?.[selectKey]
                                                            )}
                                                            onClick={() =>
                                                                row?.[selectKey] &&
                                                                handleChangeSelect(row?.[selectKey])
                                                            }
                                                        />
                                                    )}
                                                    {column?.render
                                                        ? column.render(
                                                              getValueByPath(row, column?.key),
                                                              row,
                                                              index
                                                          )
                                                        : getValueByPath(row, column?.key) || '-'}
                                                </div>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {(rows || []).length === 0 && emptyDescription && (
                <div className={styles.empty_wrapper}>
                    <Typography variant="body3">{emptyDescription}</Typography>
                </div>
            )}
            <div ref={loader} />
        </div>
    );
};

export default Table;
